import { useState, useEffect } from "react";
import { useParams, Link, useLocation, useHistory } from "react-router-dom";
import { axiosInstance } from '../../../utils/axios';
import { FiSearch, FiFilter, FiInfo, FiX, FiPaperclip, FiShare2, FiUsers, FiChevronDown, FiRefreshCcw, FiMenu } from "react-icons/fi";
import { IoMdHome } from "react-icons/io";
import { BsCheckAll } from "react-icons/bs";
import { useKeycloak } from "@react-keycloak/web";

const PageTeachersDoubts = () => {
  console.log("NEW DOUBTS PAGE MOUNTED - 2.0");
  const { classId } = useParams();
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const history = useHistory();

  const [classDetails, setClassDetails] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    return tab && ['all', 'answered', 'unanswered'].includes(tab) ? tab : 'unanswered';
  };

  const [filterTab, setFilterTab] = useState(getInitialTab() === 'all' ? 'unanswered' : getInitialTab()); // force match UN ANSWERED / ANSWERED tabs
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ all: true, single: false, group: false });

  // Modals state
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [answeringQuestion, setAnsweringQuestion] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [classId]);

  const fetchData = async () => {
    try {
      const [classRes, questionsRes] = await Promise.all([
        axiosInstance.get(`/${classId}`),
        axiosInstance.get(`/questions/${classId}`),
      ]);
      setClassDetails(classRes.data);
      setQuestions(questionsRes.data.body || []);
    } catch (error) {
      console.error("Error fetching doubts data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setFilterTab(key);
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('tab', key);
    history.replace(newUrl.pathname + newUrl.search);
  };

  const openAnswerModal = (q) => {
    setAnsweringQuestion(q);
    setAnswerText(q.answer || '');
    setShowAnswerModal(true);
  };

  const closeAnswerModal = () => {
    setShowAnswerModal(false);
    setAnsweringQuestion(null);
    setAnswerText('');
  };

  const handlePublish = async (type) => { // type: 'all' | 'group'
    if (!answeringQuestion || !answerText.trim()) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(`/questions/${answeringQuestion.id}/answer`, {
        answer: answerText,
      });
      // Refresh
      const res = await axiosInstance.get(`/questions/${classId}`);
      setQuestions(res.data.body || []);
      
      closeAnswerModal();
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error submitting answer:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredQuestions = () => {
    let filtered = [...questions];
    if (filterTab === 'answered') {
      filtered = filtered.filter(q => q.answer !== null && q.answer !== undefined);
    } else {
      filtered = filtered.filter(q => q.answer === null || q.answer === undefined);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(q2 =>
        (q2.question || '').toLowerCase().includes(q) ||
        (q2.studentName || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#110B38] text-white">
        <div className="animate-pulse">Loading doubts...</div>
      </div>
    );
  }

  const displayQuestions = filteredQuestions();

  return (
    <div className="min-h-screen bg-[#110B38] text-white font-sans">
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs mb-6 font-medium text-gray-500">
          <Link to="/teacher/home" className="text-[#6A4CFF] hover:underline flex items-center gap-1">
            <IoMdHome size={16} /> 
          </Link>
          <span>/</span>
          <Link to={`/teacher/dashboard/${classId}`} className="text-[#6A4CFF] hover:underline cursor-pointer">Teacher</Link>
          <span>/</span>
          <span className="text-gray-400">Doubts</span>
        </div>

        {/* Header Title & Search/Filter */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            Doubts <span className="text-gray-400 text-lg font-normal">- {classDetails?.subject?.title || 'Science'}</span>
          </h2>

          <div className="flex items-center gap-3 relative">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#251B5A] border-none text-white text-sm rounded-md px-4 py-2.5 pr-10 focus:outline-none focus:ring-1 focus:ring-[#6A4CFF]"
              />
              <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            
            <button 
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="bg-[#251B5A] p-2.5 rounded-md text-gray-400 hover:text-white transition-colors"
            >
              <FiFilter size={18} />
            </button>

            {/* Filter Dropdown */}
            {showFilterDropdown && (
              <div className="absolute top-12 right-0 w-48 bg-black rounded-lg shadow-xl border border-gray-800 p-4 z-20">
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={filterOptions.all} onChange={() => setFilterOptions({...filterOptions, all: !filterOptions.all})} className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded" />
                    <span className="text-sm font-bold">All</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={filterOptions.single} onChange={() => setFilterOptions({...filterOptions, single: !filterOptions.single})} className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded" />
                    <span className="text-sm text-gray-300">Single</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={filterOptions.group} onChange={() => setFilterOptions({...filterOptions, group: !filterOptions.group})} className="w-4 h-4 accent-[#6A4CFF] bg-transparent border-gray-600 rounded" />
                    <span className="text-sm text-gray-300">Group</span>
                  </label>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <button className="text-gray-400 hover:text-white"><FiRefreshCcw size={18} /></button>
                  <button className="flex-1 bg-[#6A4CFF] text-white text-xs font-bold py-2 rounded-md">APPLY</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs and Stats Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 pb-4 border-b border-[#312871] gap-4">
          <div className="flex bg-[#251B5A] p-1 rounded-lg w-fit">
            <button
              onClick={() => handleTabChange('unanswered')}
              className={`px-5 py-2 text-xs font-bold rounded-md transition-colors ${filterTab === 'unanswered' ? 'bg-[#5B46A9] text-white' : 'text-gray-400 hover:text-white'}`}
            >
              UN ANSWERED
            </button>
            <button
              onClick={() => handleTabChange('answered')}
              className={`px-5 py-2 text-xs font-bold rounded-md transition-colors ${filterTab === 'answered' ? 'bg-[#5B46A9] text-white' : 'text-gray-400 hover:text-white'}`}
            >
              ANSWERED
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-xs text-gray-400 font-medium tracking-wide">
              {questions.length} Questions <span className="mx-1">|</span> 12 Group <span className="mx-1">|</span> <span className="text-pink-400 font-bold">28 Single</span>
            </div>
            {filterTab === 'answered' && (
              <button className="bg-white text-black px-4 py-2 font-bold text-[11px] rounded-md shadow-sm uppercase">
                Group Questions
              </button>
            )}
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayQuestions.length > 0 ? displayQuestions.map((question, i) => {
            const isUnanswered = filterTab === 'unanswered';
            // Styling logic based on tab
            const cardBg = isUnanswered ? 'bg-[#553CBB]' : 'bg-[#005252]';
            const badgeBg = isUnanswered ? 'border border-white/30 text-white' : 'bg-transparent text-[#26C281] font-bold';
            const badgeText = isUnanswered ? 'UN ANSWERED' : 'ANSWERED';
            const mainSolidBtnBg = isUnanswered ? 'bg-white text-[#553CBB]' : 'bg-white text-[#005252]';
            const mainBtnLabel = isUnanswered ? 'Answer Now' : 'Publish All';
            const subTitleText = isUnanswered ? '1 Question' : '8 Questions'; // mockup groups

            return (
              <div key={question.id || i} className={`${cardBg} rounded-[20px] p-5 shadow-lg flex flex-col h-full transform transition-transform hover:scale-[1.02]`}>
                {/* Header Profile + Badge */}
                <div className="flex justify-between items-start mb-4">
                  {isUnanswered ? (
                    <img src="https://ui-avatars.com/api/?name=A&background=random" className="w-8 h-8 rounded-full border-2 border-white/20" alt="Student" />
                  ) : (
                    <div className="flex items-center">
                      <div className="flex -space-x-2">
                        <img src="https://ui-avatars.com/api/?name=A" className="w-8 h-8 rounded-full border-2 border-[#005252] z-30" alt="st" />
                        <img src="https://ui-avatars.com/api/?name=B" className="w-8 h-8 rounded-full border-2 border-[#005252] z-20" alt="st" />
                        <img src="https://ui-avatars.com/api/?name=C" className="w-8 h-8 rounded-full border-2 border-[#005252] z-10" alt="st" />
                      </div>
                      <span className="text-white text-[11px] font-bold ml-2 tracking-wider">+5</span>
                    </div>
                  )}
                  <span className={`px-3 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${badgeBg}`}>
                    {badgeText}
                  </span>
                </div>

                {/* Question Info */}
                <div className="mb-4 flex-1">
                  <h3 className="text-white font-bold text-base">{classDetails?.subject?.title || 'Solar System'}</h3>
                  <p className="text-white/70 italic text-xs mb-3">{subTitleText}</p>
                  <p className="text-white text-sm font-medium leading-relaxed line-clamp-3">
                    <span className="font-bold mr-1">Q.</span>
                    {question.question || question.text || 'How many planets in Solar System?, How many planets are there in the...'}
                  </p>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center gap-3 mt-auto pt-2">
                  <button 
                    onClick={() => openAnswerModal(question)}
                    className="flex-1 py-2 border border-white/40 text-white rounded-lg text-xs font-bold transition-colors hover:bg-white/10"
                  >
                    {isUnanswered ? 'View All' : 'View'}
                  </button>
                  <button 
                    onClick={() => openAnswerModal(question)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors shadow-md ${mainSolidBtnBg}`}
                  >
                    {mainBtnLabel}
                  </button>
                </div>
              </div>
            )
          }) : (
            <div className="col-span-full py-16 text-center text-gray-400">
              No doubts found in this category.
            </div>
          )}
        </div>
      </div>

      {/* ANSWER MODAL OVERLAY */}
      {showAnswerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#09051C]/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-[#3A2A82] rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
            
            {/* Modal Header */}
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <h3 className="text-white font-bold text-lg italic flex items-center gap-2">
                Student&apos;s question 
              </h3>
              <div className="flex items-center gap-4 text-white/50">
                <FiInfo size={18} className="cursor-pointer hover:text-white" />
                <FiX size={24} className="cursor-pointer hover:text-white" onClick={closeAnswerModal} />
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[80vh]">
              
              <div className="flex justify-between items-end mb-4">
                <h4 className="text-white font-bold text-lg">List of question</h4>
                <div className="w-10 h-10 rounded-full border-2 border-red-500 flex items-center justify-center text-red-500 text-xs font-bold bg-black/20">
                  58%
                </div>
              </div>

              {/* Mock Student Questions List */}
              <div className="space-y-3 mb-8">
                {[
                  "What are ionic compounds?",
                  "What is the melting point of ionic compounds?",
                  "Why do ionic compunds have high melting poinst?",
                  "What are the properties of ionic compounds?",
                  "Why are ionic compounds stable?"
                ].map((q, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 text-white">
                    <div className="flex items-start gap-3 flex-1">
                      <img src={`https://ui-avatars.com/api/?name=${i}&background=random`} className="w-6 h-6 rounded-full mt-0.5" alt="stu" />
                      <p className="text-sm">{i + 1}. {i === 0 ? answeringQuestion?.question || q : q}</p>
                    </div>
                    <button className="text-white/40 hover:text-white mt-0.5">
                      <FiX size={16} className="bg-white/10 rounded-full p-0.5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Answer Input Area */}
              <div className="bg-[#1C1154] rounded-xl p-4 border border-[#48359F] relative group transition-all focus-within:ring-2 focus-within:ring-[#6A4CFF]">
                <p className="text-white font-bold text-xs italic mb-2">Answer :</p>
                <textarea
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  placeholder="Type your answer..."
                  className="w-full bg-transparent text-white text-sm focus:outline-none resize-none min-h-[80px]"
                />
                
                {/* Input Footer toolbar */}
                <div className="flex justify-between items-center mt-4">
                  <div className="flex items-center cursor-pointer">
                    <div className="flex -space-x-2">
                      <img src="https://ui-avatars.com/api/?name=A" className="w-6 h-6 rounded-full border border-[#1C1154] z-30" alt="st" />
                      <img src="https://ui-avatars.com/api/?name=B" className="w-6 h-6 rounded-full border border-[#1C1154] z-20" alt="st" />
                      <img src="https://ui-avatars.com/api/?name=C" className="w-6 h-6 rounded-full border border-[#1C1154] z-10" alt="st" />
                    </div>
                    <span className="text-white text-[10px] font-bold ml-2">+5</span>
                  </div>
                  <button className="w-8 h-8 rounded-full bg-[#3A2A82] flex items-center justify-center text-white/70 hover:text-white transition-colors">
                    <FiPaperclip size={14} />
                  </button>
                </div>
              </div>

              {/* Source Link */}
              <div className="flex justify-end mt-2 mb-4">
                <button className="text-[10px] text-white/50 hover:text-white flex items-center gap-1">
                  Source from your uploaded documents View Source <FiChevronDown />
                </button>
              </div>

              {/* Generate Config */}
              <div className="w-full bg-[#5263C6]/20 py-3 rounded-lg flex items-center justify-center cursor-pointer hover:bg-[#5263C6]/30 transition-colors mb-6 border border-[#5263C6]/50">
                <span className="text-[#AAB6FF] text-sm font-bold">Generate Using Internet</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button 
                  disabled={submitting}
                  onClick={() => handlePublish('all')}
                  className="flex-1 bg-[#6A4CFF] hover:bg-[#5E40BF] text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <FiShare2 size={18} /> Publish to all
                </button>
                <button 
                  disabled={submitting}
                  onClick={() => handlePublish('group')}
                  className="flex-1 bg-[#FFD700] hover:bg-[#E6C200] text-[#110B38] py-3 rounded-xl font-bold flex justify-center items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <FiUsers size={18} /> Publish to group
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* SUCCESS CONFIRMATION MODAL */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#09051C]/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-[#3A2A82] rounded-2xl w-full max-w-sm shadow-2xl flex flex-col overflow-hidden animate-scale-in text-center pb-8 border border-white/10">
            <div className="flex justify-between items-center p-4">
              <h3 className="text-white font-bold italic w-full text-left ml-2">Congratulations !</h3>
              <FiX size={20} className="text-white/50 cursor-pointer hover:text-white" onClick={() => setShowSuccessModal(false)} />
            </div>
            <div className="border-b border-white/10 mb-8" />
            
            <div className="flex justify-center mb-6">
              <BsCheckAll size={64} className="text-[#26C281]" />
            </div>
            
            <p className="text-white font-medium mb-8">Published to 15 Students</p>
            
            <div className="px-12">
              <button 
                onClick={() => setShowSuccessModal(false)}
                className="w-full bg-[#251B5A] hover:bg-[#1C1154] border border-[#48359F] text-white py-2.5 rounded-lg font-bold text-sm transition-colors shadow-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PageTeachersDoubts;
